/**
 * OrganizationContext.js - Contexto simplificado para branding
 * NO hace llamadas API - recibe datos de /api/user/config via updateBrandingFromUserConfig
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// Valores por defecto (Data Bunker)
const DEFAULT_BRANDING = {
    id: 'default',
    name: 'default',
    display_name: 'Data Bunker',
    logo_url: null,
    logo_login_url: null,
    favicon_url: '/favicon.png',
    primary_color: '#073C5C',
    secondary_color: '#F68D2E',
    text_color: '#1f2937',
    background_color: '#f8fafc',
    page_title: 'Data Bunker',
    app_title: 'Motor de Optimización de Precios con IA',
    welcome_message: null
};

const OrganizationContext = createContext(null);

export const OrganizationProvider = ({ children }) => {
    const [branding, setBranding] = useState(DEFAULT_BRANDING);
    const [loading, setLoading] = useState(false);

    // Aplicar branding dinámicamente al DOM
    const applyDynamicBranding = useCallback((brandingData) => {
        // 1. Actualizar título de la página
        if (brandingData.page_title) {
            document.title = brandingData.page_title;
        }

        // 2. Actualizar favicon
        if (brandingData.favicon_url) {
            const favicon = document.querySelector('link[rel="icon"]');
            if (favicon) {
                favicon.href = brandingData.favicon_url;
            }
        }

        // 3. Actualizar CSS variables en :root
        const root = document.documentElement;
        if (brandingData.primary_color) {
            root.style.setProperty('--org-primary', brandingData.primary_color);
            root.style.setProperty('--org-primary-light', lightenColor(brandingData.primary_color, 20));
            const primaryRgb = hexToRgb(brandingData.primary_color);
            if (primaryRgb) {
                root.style.setProperty('--org-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
            }
        }
        if (brandingData.secondary_color) {
            root.style.setProperty('--org-secondary', brandingData.secondary_color);
            const secondaryRgb = hexToRgb(brandingData.secondary_color);
            if (secondaryRgb) {
                root.style.setProperty('--org-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
            }
        }
        if (brandingData.text_color) {
            root.style.setProperty('--org-text', brandingData.text_color);
        }
        if (brandingData.background_color) {
            root.style.setProperty('--org-background', brandingData.background_color);
        }
    }, []);

    // Método para actualizar branding desde userConfig (llamado por App.js después de /api/user/config)
    const updateBrandingFromUserConfig = useCallback((userConfig) => {
        if (!userConfig) {
            setBranding(DEFAULT_BRANDING);
            return;
        }

        // Obtener datos de branding de userConfig
        const orgBranding = userConfig.organization?.branding || {};
        const brandingFromConfig = {
            ...DEFAULT_BRANDING,
            logo_url: orgBranding.logoUrl || userConfig.organization?.urlImage,
            logo_login_url: orgBranding.logoLoginUrl || orgBranding.logoUrl || userConfig.organization?.urlImage,
            favicon_url: orgBranding.faviconUrl || DEFAULT_BRANDING.favicon_url,
            primary_color: orgBranding.primaryColor || DEFAULT_BRANDING.primary_color,
            secondary_color: orgBranding.secondaryColor || DEFAULT_BRANDING.secondary_color,
            text_color: orgBranding.textColor || DEFAULT_BRANDING.text_color,
            background_color: orgBranding.backgroundColor || DEFAULT_BRANDING.background_color,
            page_title: orgBranding.pageTitle || userConfig.organization?.displayName || DEFAULT_BRANDING.page_title,
            app_title: orgBranding.appTitle || DEFAULT_BRANDING.app_title,
            welcome_message: orgBranding.welcomeMessage,
            display_name: userConfig.organization?.displayName || DEFAULT_BRANDING.display_name
        };

        setBranding(brandingFromConfig);
        applyDynamicBranding(brandingFromConfig);
        console.log('[ORG] Branding actualizado desde userConfig:', brandingFromConfig.display_name);
    }, [applyDynamicBranding]);

    // Resetear a defaults (cuando se cierra sesión)
    const resetBranding = useCallback(() => {
        setBranding(DEFAULT_BRANDING);
        applyDynamicBranding(DEFAULT_BRANDING);
    }, [applyDynamicBranding]);

    const value = {
        // Estado
        branding,
        loading,
        orgConfig: null, // Ya no se usa, mantenido por compatibilidad

        // Acciones
        updateBrandingFromUserConfig,
        resetBranding,

        // Helpers para acceso directo
        logoUrl: branding.logo_url,
        loginLogoUrl: branding.logo_login_url || branding.logo_url,
        faviconUrl: branding.favicon_url,
        primaryColor: branding.primary_color,
        secondaryColor: branding.secondary_color,
        pageTitle: branding.page_title,
        appTitle: branding.app_title,
        displayName: branding.display_name,
        welcomeMessage: branding.welcome_message,

        // Verificar si es branding por defecto
        isDefaultBranding: branding.id === 'default'
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};

// Custom hook para usar el contexto de organización
export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};

// Hook con valor seguro (no lanza error si no hay provider)
export const useOrganizationSafe = () => {
    const context = useContext(OrganizationContext);
    return context || {
        branding: DEFAULT_BRANDING,
        loading: false,
        logoUrl: null,
        primaryColor: DEFAULT_BRANDING.primary_color,
        secondaryColor: DEFAULT_BRANDING.secondary_color,
        isDefaultBranding: true
    };
};

// ============================================================================
// UTILIDADES DE COLOR
// ============================================================================

function hexToRgb(hex) {
    if (!hex) return null;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

function lightenColor(hex, percent) {
    if (!hex) return hex;
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const lighter = {
        r: Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * (percent / 100))),
        g: Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * (percent / 100))),
        b: Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * (percent / 100)))
    };

    return `#${lighter.r.toString(16).padStart(2, '0')}${lighter.g.toString(16).padStart(2, '0')}${lighter.b.toString(16).padStart(2, '0')}`;
}

export default OrganizationContext;
